import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatError } from "@/lib/api";
import { ZELLE_QR } from "@/lib/assets";
import { HeartHandshake, Gift, GraduationCap } from "lucide-react";

const initial = { name: "", email: "", phone: "", type: "donation", amount: "", student: "", message: "" };

export default function Donation() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const isSponsor = form.type === "sponsor";
      const parts = [isSponsor ? t.donate.type_sponsor : t.donate.type_donation];
      if (form.amount) parts.push(form.amount);
      if (isSponsor && form.student) parts.push(form.student);
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        type: isSponsor ? "sponsor" : "donation",
        program: parts.join(" · "),
        message: form.message,
      });
      toast.success(t.donate.success);
      setForm(initial);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const field = "w-full h-12 bg-transparent border-b border-icdh-white/30 focus:border-icdh-red outline-none text-icdh-white placeholder:text-icdh-white/40 transition-colors";
  const isSponsor = form.type === "sponsor";

  return (
    <section id="donate" data-testid="donate-section" className="relative bg-icdh-navy text-icdh-white py-28 md:py-40">
      <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal><Overline dark>{t.donate.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">{t.donate.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-icdh-white/75 text-lg max-w-2xl">{t.donate.lead}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-8 items-start">
          {/* Zelle instant */}
          <Reveal delay={0.2}>
            <div data-testid="donate-zelle" className="bg-icdh-white text-icdh-navy p-8 md:p-10 flex flex-col">
              <div className="flex items-center gap-3">
                <HeartHandshake className="text-icdh-red" />
                <p className="text-xs uppercase tracking-[0.28em] text-icdh-red">{t.donate.pay_now}</p>
              </div>
              <p className="mt-4 text-icdh-navy/70 text-sm">{t.donate.pay_hint}</p>
              <div className="mt-6 bg-icdh-surface border border-icdh-navy/10 flex items-center justify-center p-4">
                <img src={ZELLE_QR} alt="Zelle QR" data-testid="donate-zelle-qr" className="w-full max-w-[320px] h-auto object-contain" />
              </div>
              <p className="mt-4 text-center text-xs uppercase tracking-[0.25em] text-icdh-navy/70">Zelle · icdhhaiti@gmail.com</p>
            </div>
          </Reveal>

          {/* Pledge form */}
          <Reveal delay={0.3}>
            <form onSubmit={submit} data-testid="donate-form" className="space-y-7">
              <p className="text-xs uppercase tracking-[0.25em] text-icdh-red">{t.donate.or}</p>

              {/* Type picker */}
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-icdh-white/60 mb-3">{t.donate.type_label}</p>
                <div className="grid grid-cols-2 gap-3" data-testid="donate-type">
                  <button type="button" onClick={() => setForm({ ...form, type: "donation" })} data-testid="donate-type-donation"
                    className={`h-14 px-4 flex items-center gap-3 border text-sm font-medium transition-colors ${form.type === "donation" ? "bg-icdh-red border-icdh-red text-icdh-white" : "border-icdh-white/25 text-icdh-white hover:border-icdh-red"}`}>
                    <Gift size={16} />{t.donate.type_donation}
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, type: "sponsor" })} data-testid="donate-type-sponsor"
                    className={`h-14 px-4 flex items-center gap-3 border text-sm font-medium transition-colors ${form.type === "sponsor" ? "bg-icdh-red border-icdh-red text-icdh-white" : "border-icdh-white/25 text-icdh-white hover:border-icdh-red"}`}>
                    <GraduationCap size={16} />{t.donate.type_sponsor}
                  </button>
                </div>
              </div>

              <input required data-testid="donate-name" className={field} placeholder={t.admissions.form.name} value={form.name} onChange={set("name")} />
              <input required type="email" data-testid="donate-email" className={field} placeholder={t.admissions.form.email} value={form.email} onChange={set("email")} />
              <input data-testid="donate-phone" className={field} placeholder={t.admissions.form.phone} value={form.phone} onChange={set("phone")} />
              <input data-testid="donate-amount" className={field} placeholder={t.donate.amount_placeholder} value={form.amount} onChange={set("amount")} />

              {isSponsor && (
                <input data-testid="donate-student" className={field} placeholder={t.donate.student_placeholder} value={form.student} onChange={set("student")} />
              )}

              <textarea rows={3} data-testid="donate-message" className={`${field} h-auto resize-none pt-3`} placeholder={t.donate.message_placeholder} value={form.message} onChange={set("message")} />

              <button type="submit" disabled={sending} data-testid="donate-submit"
                className="w-full h-14 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-white hover:text-icdh-navy transition-colors duration-300 disabled:opacity-60">
                {sending ? "..." : t.donate.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
